import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../Shared/ConfirmationModal/ConfirmationModal';
import { CertStateReview } from '../../../utils/api/certification';
import inspectionReviewApi from '../../../utils/api/inspectionReview';
import RejectReviewModal from './RejectReviewModal';
import './_ReviewActions.scss';

type Props = {
  review: CertStateReview | null | undefined;
  /** Called after a successful reject/cancel so the caller can refetch cert state. */
  onChanged: () => void;
  /** SW2-2248 (Oct 9): the email's Reject link asked for the reject dialog. */
  openReject?: boolean;
};

/**
 * SW2-2248 — the reviewer's "send back for revision" button and the submitter's "cancel review"
 * button. Deliberately no "Approve" button here: signing the `inspectionReview` signature line via
 * the ordinary certification flow (`CertificationForm`, already rendered for
 * `signatureGroups.inspection`) IS the approval — see
 * `InspectionReviewController.approve`'s own doc comment ("that is what the frontend uses"). A
 * second approve path here would duplicate, not add to, that flow.
 *
 * `review.canSign`/`review.canCancel` are precomputed server-side per viewer — see
 * `inspectionCertificationService.buildReviewBlock` — so neither button re-derives its own
 * permission check.
 */
const ReviewActions: React.FC<Props> = ({ review, onChanged, openReject = false }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    if (openReject && review?.canSign) setShowRejectModal(true);
  }, [openReject, review?.canSign]);

  if (review == null || review.status !== 'pending' || review.id == null) {
    return null;
  }

  const handleCancel = () => {
    inspectionReviewApi
      .cancel(review.id as number)
      .then(() => {
        toast.success('Review request cancelled. Your signature was removed; sign again to resubmit.');
        setShowCancelConfirm(false);
        onChanged();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to cancel the review request');
      });
  };

  return (
    <div className='review-actions'>
      {review.canSign && (
        <button
          className='bg-neutral-200 review-actions__button'
          onClick={() => setShowRejectModal(true)}
        >
          Send Back for Revision
        </button>
      )}

      {review.canCancel && (
        <button
          className='bg-neutral-200 review-actions__button'
          onClick={() => setShowCancelConfirm(true)}
        >
          Cancel Review Request
        </button>
      )}

      <RejectReviewModal
        show={showRejectModal}
        reviewId={review.id}
        onClose={() => setShowRejectModal(false)}
        onSuccess={onChanged}
      />

      <ConfirmationModal
        show={showCancelConfirm}
        handleClose={() => setShowCancelConfirm(false)}
        handleConfirm={handleCancel}
        title='Cancel Review Request'
        subTitle='Withdraw this review request? Your inspection signature will be removed so you can make changes. Signing the inspection again sends it for review again.'
        buttonText='Cancel Review Request'
      />
    </div>
  );
};

export default ReviewActions;
